import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { TreeSpeciesDto } from '@lib/dto/tree-species.dto'
import type {
  CreateTreeSpeciesInput,
  UpdateTreeSpeciesInput,
} from '@lib/input/tree-species.input'
import { treeSpeciesService } from '@/services/TreeSpeciesService'
import { treeSpeciesKeys, treeKeys } from '@/hooks/queryKeys'

/**
 * The species catalog.
 *
 * A long `staleTime`: the list changes a handful of times a year, and the
 * survey screen needs it on every mount to draw a picker. Refetching it each
 * time somebody opens a map would be work spent on a list that has not moved.
 */
export function useTreeSpecies(): UseQueryResult<TreeSpeciesDto[]> {
  return useQuery({
    queryKey: treeSpeciesKeys.list(),
    queryFn: async () => (await treeSpeciesService.list()).species,
    staleTime: 5 * 60_000,
  })
}

/**
 * Everything that changes the catalog also invalidates trees.
 *
 * Trees carry their species' mark inline so a pin can draw itself without a
 * second lookup — which means renaming or re-coloring a species leaves every
 * cached tree holding the old one.
 */
function useSpeciesMutation<TInput>(
  run: (input: TInput) => Promise<TreeSpeciesDto>,
): UseMutationResult<TreeSpeciesDto, Error, TInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: treeSpeciesKeys.all })
      void queryClient.invalidateQueries({ queryKey: treeKeys.all })
    },
  })
}

export function useCreateTreeSpecies(): UseMutationResult<
  TreeSpeciesDto,
  Error,
  CreateTreeSpeciesInput
> {
  return useSpeciesMutation((input) => treeSpeciesService.create(input))
}

export function useUpdateTreeSpecies(): UseMutationResult<
  TreeSpeciesDto,
  Error,
  { id: string; input: UpdateTreeSpeciesInput }
> {
  return useSpeciesMutation(({ id, input }) =>
    treeSpeciesService.update(id, input),
  )
}

export function useArchiveTreeSpecies(): UseMutationResult<
  TreeSpeciesDto,
  Error,
  { id: string; archived: boolean }
> {
  return useSpeciesMutation(({ id, archived }) =>
    treeSpeciesService.setArchived(id, archived),
  )
}
